import {isEmpty} from 'lodash';
import moment from 'moment';
import {useContext, useEffect, useMemo, useState} from 'react';

import {workingHoursApi} from 'api';
import {AssignmentSelectors, DataWrapper, DatePicker, Icon} from 'components';
import {UserContext} from 'context';
import {useApiData} from 'hooks';

import {BarTimeline} from './BarTimeline';
import {UsersTimeline} from './UsersTimeline';

enum SelectedView {
    Bars = 'bars',
    Timeline = 'timeline'
}

export const WorkingHoursTimeline: React.FC = () => {
    const {settings} = useContext(UserContext);
    const {dateFormat, timeZone} = useMemo(() => settings.common, [settings.common]);
    const [groupId, setGroupId] = useState('');
    const [dateMoment, setDateMoment] = useState(moment().tz(timeZone));
    const [selectedView, setSelectedView] = useState(SelectedView.Bars);

    const {
        send: fetchTimeline,
        data: {usersIntervals, assignmentIntervals} = {usersIntervals: [], assignmentIntervals: []},
        error,
        loading
    } = useApiData(workingHoursApi.getTimelineData, {loadingDefault: true});

    useEffect(() => {
        const date = dateMoment.format(dateFormat);
        fetchTimeline(date, groupId);
    }, [dateFormat, dateMoment, fetchTimeline, groupId]);

    return (
        <div className='tw-relative tw-mr-4'>
            <div className='sm:tw-flex tw-mb-4'>
                <AssignmentSelectors
                    emptyGroupOption='all'
                    selectedGroupId={groupId}
                    onChange={setGroupId}
                >
                    {({renderGroup}) => (
                        <div className='tw-mb-4 sm:tw-w-64'>
                            {renderGroup()}
                        </div>
                    )}
                </AssignmentSelectors>
                <div className='tw-flex tw-flex-grow'>
                    <DatePicker
                        className='sm:tw-w-48 sm:tw-ml-4 -sm:tw-mb-4'
                        overlayClassName='-sm:tw-left-1/2 -sm:tw--translate-x-1/2'
                        openDirection='bottom'
                        value={dateMoment}
                        format={dateFormat}
                        onChange={setDateMoment}
                    />
                    <div className='tw-flex -sm:tw-ml-8 tw-ml-auto tw-items-center'>
                        <Icon
                            name='bar-chart'
                            button
                            group
                            size='lg'
                            color={selectedView === SelectedView.Bars ? 'dark' : 'default-dimmed'}
                            onClick={() => setSelectedView(SelectedView.Bars)}
                        />
                        <Icon
                            name='table-view'
                            button
                            group
                            size='lg'
                            color={selectedView === SelectedView.Timeline ? 'dark' : 'default-dimmed'}
                            className='tw-ml-2'
                            onClick={() => setSelectedView(SelectedView.Timeline)}
                        />
                    </div>
                </div>
            </div>

            <DataWrapper isLoading={loading} isError={!!error} noData={isEmpty(usersIntervals)}>
                {selectedView === SelectedView.Bars &&
                    <BarTimeline
                        usersIntervals={usersIntervals}
                        assignmentIntervals={assignmentIntervals}
                        timeZone={timeZone}
                    />}
                {selectedView === SelectedView.Timeline &&
                    <UsersTimeline usersIntervalsData={usersIntervals} timeZone={timeZone} />}
            </DataWrapper>
        </div>
    );
};